import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import {
  NorthEastRounded,
  EastRounded,
  SouthEastRounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ProgressBar } from "./ClientUiViewClient";
import type { StatItem } from "./StickyStatsViewClient";
import {
  formatMoney,
  formatNumber,
  formatPercent,
  formatSignedPercent,
  GoalSource,
  goalPacing,
  metricSeriesColor,
  PaceOutcome,
  paceOutcome,
} from "./clientFormatViewClient";

// Paneles de cifras de una campaña o subcampaña: lo que va junto, en una
// misma tarjeta. Resultados en una fila, la inversión con su presupuesto, los
// costes por unidad juntos y el objetivo con por dónde se debería ir.

const cardSx = (paper: string, divider: string) =>
  ({
    backgroundColor: paper,
    border: `1px solid ${divider}`,
    borderRadius: "20px",
    minWidth: 0,
  }) as const;

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: 13,
      color: "text.secondary",
      mb: 0.75,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {children}
  </Typography>
);

const Value: React.FC<{ children: React.ReactNode; large?: boolean }> = ({
  children,
  large,
}) => (
  <Typography
    sx={{
      fontSize: large ? { xs: 28, md: 34 } : { xs: 22, md: 26 },
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Typography>
);

const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75 }}>
    {children}
  </Typography>
);

/** Cifras relacionadas en una sola tarjeta, una al lado de otra con una raya. */
export const StatStrip: React.FC<{ items: StatItem[] }> = ({ items }) => {
  const theme = useTheme();
  if (items.length === 0) return null;
  return (
    <Box
      sx={{
        ...cardSx(theme.palette.background.paper, theme.palette.divider),
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        overflow: "hidden",
      }}
    >
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            px: 3,
            py: 2.5,
            minWidth: 0,
            // La raya a la izquierda de cada una; la del borde no se ve.
            boxShadow: `-1px 0 0 ${theme.palette.divider}`,
          }}
        >
          <Label>{item.label}</Label>
          <Value>{item.value}</Value>
          {item.hint && <Hint>{item.hint}</Hint>}
        </Box>
      ))}
    </Box>
  );
};

/**
 * La inversión y el presupuesto, juntos: lo invertido a un lado, cuánto lleva
 * del presupuesto al otro y, uniéndolos, la barra de lo gastado.
 */
export const BudgetPanel: React.FC<{
  investment: number;
  budget: number | null;
  currency: string;
}> = ({ investment, budget, currency }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = i18n.language;
  const money = (v: number) => formatMoney(v, currency, language);
  return (
    <Box
      sx={{
        ...cardSx(theme.palette.background.paper, theme.palette.divider),
        px: 3,
        py: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Label>{t("clientView.stats.investment")}</Label>
          <Value>{money(investment)}</Value>
        </Box>
        {budget && (
          <Box sx={{ minWidth: 0, textAlign: "right" }}>
            <Label>{t("clientView.money.budgetSpent")}</Label>
            <Value>{formatPercent(investment / budget, language)}</Value>
          </Box>
        )}
      </Box>
      {budget ? (
        <>
          <Box sx={{ mt: 2 }}>
            <ProgressBar
              value={investment / budget}
              color={metricSeriesColor("investment", 0, theme.palette.mode)}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Hint>
              {t("clientView.money.ofBudget", { budget: money(budget) })}
            </Hint>
            <Hint>
              {t("clientView.money.budgetLeft", {
                amount: money(Math.max(budget - investment, 0)),
              })}
            </Hint>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

// Para el cliente ir por delante es buena noticia: verde en ritmo, azul por
// delante y rojo por detrás (las mismas cuentas y márgenes que el WIP).
const PACE_COLOR: Record<PaceOutcome, string> = {
  behind: "#ef4444",
  onTrack: "#10b981",
  ahead: "#2563eb",
};
const PACE_ARROW: Record<PaceOutcome, typeof EastRounded> = {
  behind: SouthEastRounded,
  onTrack: EastRounded,
  ahead: NorthEastRounded,
};

/** Pastilla de ritmo: flecha, en qué queda y cuánto (+8 %, −4 %). */
const PaceChip: React.FC<{
  variation: number;
  labels: Record<PaceOutcome, string>;
}> = ({ variation, labels }) => {
  const { i18n } = useTranslation();
  const outcome = paceOutcome(variation);
  const color = PACE_COLOR[outcome];
  const Arrow = PACE_ARROW[outcome];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
        color,
        backgroundColor: alpha(color, 0.12),
      }}
    >
      <Arrow sx={{ fontSize: 14 }} />
      {labels[outcome]} · {formatSignedPercent(variation, i18n.language)}
    </Box>
  );
};

/**
 * El objetivo: cuánto se lleva, la barra con una raya por donde se debería ir
 * hoy, la desviación frente a eso y dónde acabará al ritmo de ayer (como la
 * columna "Proyección" del WIP).
 */
export const GoalPanel: React.FC<{
  goal: GoalSource;
  kpiLabel: string;
}> = ({ goal, kpiLabel }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = i18n.language;
  const pacing = goalPacing(goal);
  if (!pacing || goal.planned === null) return null;
  const kpi = kpiLabel.toLowerCase();

  const facts: { key: string; label: string; content: React.ReactNode }[] = [];
  if (pacing.expected !== null)
    facts.push({
      key: "expected",
      label: t("clientView.goal.expected"),
      content: <Value>{formatPercent(pacing.expected, language)}</Value>,
    });
  if (pacing.deviation !== null)
    facts.push({
      key: "deviation",
      label: t("clientView.goal.deviation"),
      content: (
        <>
          <Value>{formatSignedPercent(pacing.deviation, language)}</Value>
          <Box sx={{ mt: 1 }}>
            <PaceChip
              variation={pacing.deviation}
              labels={{
                behind: t("clientView.goal.behind"),
                onTrack: t("clientView.goal.onTrack"),
                ahead: t("clientView.goal.ahead"),
              }}
            />
          </Box>
        </>
      ),
    });
  if (pacing.expected !== null)
    facts.push({
      key: "projection",
      label: t("clientView.goal.projection"),
      content:
        pacing.projected !== null && pacing.projectedVolume !== null ? (
          <>
            <Value>{formatPercent(pacing.projected, language)}</Value>
            <Box sx={{ mt: 1 }}>
              <PaceChip
                variation={pacing.projected - 1}
                labels={{
                  behind: t("clientView.goal.belowTarget"),
                  onTrack: t("clientView.goal.onTarget"),
                  ahead: t("clientView.goal.aboveTarget"),
                }}
              />
            </Box>
            <Hint>
              {t("clientView.goal.projectionHint", {
                volume: formatNumber(pacing.projectedVolume, language),
                planned: formatNumber(goal.planned, language),
                kpi,
              })}
            </Hint>
          </>
        ) : (
          <Hint>{t("clientView.goal.noProjection")}</Hint>
        ),
    });

  return (
    <Box
      sx={{
        ...cardSx(theme.palette.background.paper, theme.palette.divider),
        px: 3,
        py: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Label>{t("clientView.stats.goalReached")}</Label>
          <Value large>{formatPercent(pacing.reached, language)}</Value>
        </Box>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {t("clientView.stats.goalReachedHint", {
            delivered: formatNumber(goal.delivered, language),
            planned: formatNumber(goal.planned, language),
            kpi,
          })}
        </Typography>
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <ProgressBar
          value={pacing.reached}
          glow
          marker={
            pacing.expected !== null
              ? {
                  value: pacing.expected,
                  label: t("clientView.goal.expectedMarker", {
                    value: formatPercent(pacing.expected, language),
                  }),
                }
              : undefined
          }
        />
      </Box>
      {facts.length > 0 && (
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 2,
          }}
        >
          {facts.map((fact) => (
            <Box key={fact.key} sx={{ minWidth: 0 }}>
              <Label>{fact.label}</Label>
              {fact.content}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Todas las cifras en su sitio: los resultados en una fila y, debajo, la
 * inversión con su presupuesto al lado de los costes por unidad.
 */
export const StatPanels: React.FC<{
  results: StatItem[];
  investment: number;
  budget: number | null;
  currency: string;
  costs: StatItem[];
}> = ({ results, investment, budget, currency, costs }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <StatStrip items={results} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          lg: costs.length > 0 ? "minmax(0, 5fr) minmax(0, 7fr)" : "1fr",
        },
        gap: 2,
      }}
    >
      <BudgetPanel
        investment={investment}
        budget={budget}
        currency={currency}
      />
      <StatStrip items={costs} />
    </Box>
  </Box>
);
